/* eslint-disable unicorn/no-useless-undefined */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import DatePicker from 'react-datepicker'
import { format, addHours } from 'date-fns'
import { ColorSelect, Textarea } from 'src/components/common'
import { Select } from 'src/components/select'
import InputField from 'src/components/formik/input-field'
import {
  getClassroomsRequest,
  getStudentsListRequest,
} from 'src/services/api.service'
import { useAuth } from 'src/contexts/auth.context'
import { useNotification } from 'src/contexts/notification.context'

import { routes } from 'src/navigation/routes'
import { ROLES } from 'src/tools'

import 'react-datepicker/dist/react-datepicker.css'

const EventSchema = Yup.object().shape({
  title: Yup.string().required(),
  // content: Yup.string(),
  // note: Yup.string(),
  // started_at: Yup.string(),
  // finished_at: Yup.string(),
  // color: Yup.string(),
  // priority: Yup.string(),
})

const ScheduleForm = ({ onSubmit, initialValues }) => {
  const { user } = useAuth()
  const { wrapCallback } = useNotification()
  const [classrooms, setClassrooms] = useState([])
  const [users, setUsers] = useState([])

  const formik = useFormik({
    initialValues,
    validationSchema: EventSchema,
    onSubmit,
  })

  useEffect(() => {
    wrapCallback(async () => {
      if (user.role_id !== ROLES.student) {
        fetchClassrooms()
      }
    })
  }, [])

  useEffect(() => {
    if (!formik?.values?.started_at) return

    formik.setFieldValue('finished_at', addHours(formik.values.started_at, 1))
  }, [formik.values.started_at])

  useEffect(() => {
    if (!formik.values.classroom_id) {
      formik.setFieldValue('user_id', undefined)
      return
    }

    fetchUsers(formik.values.classroom_id)
  }, [formik.values.classroom_id])

  const fetchClassrooms = async () => {
    const result = await getClassroomsRequest()
    setClassrooms(result.data.items)
    return true
  }

  const fetchUsers = async (classroomId) => {
    const result = await getStudentsListRequest({ classroom_id: classroomId })
    setUsers(result.data.items)
    return true
  }

  const handleSubmit = () => {
    console.log('handleSubmit', formik.isSubmitting, onSubmit)

    if (formik.isSubmitting) return

    // formik.setFieldValue('status', 'in review')

    formik.submitForm()
  }

  return (
    <div className="sectionHb-block worksheetsHb">
      <div className="sectionHb-block__wrap">
        <form onSubmit={formik.handleSubmit}>
          <div className="row">
            <div className="col-12 col-md-6">
              <InputField formik={formik} name="title">
                <label>Title</label>
                <input
                  name="title"
                  onChange={formik.handleChange}
                  value={formik.values.title}
                  type="text"
                />
              </InputField>
            </div>

            <div className="col-12 col-md-6">
              <InputField formik={formik} name="color">
                <label>Select color</label>
                <ColorSelect
                  value={formik.values.color}
                  onChange={(val) => {
                    formik.setFieldValue('color', val)
                  }}
                />
              </InputField>
            </div>
          </div>

          {user.role_id === ROLES.teacher && (
            <div className="row">
              <div className="col-12 col-md-6">
                <InputField formik={formik} name="classroom_id">
                  <label>Classroom</label>
                  <Select
                    value={formik.values.classroom_id}
                    onChange={(item) => {
                      formik.setFieldValue('classroom_id', item?.value)
                    }}
                    placeholder="Classrooms"
                    options={[
                      { label: 'No classroom', value: undefined },
                      ...classrooms.map((x) => ({
                        label: x.name,
                        value: x.id,
                      })),
                    ]}
                  />
                </InputField>
              </div>

              {formik.values.classroom_id && users && (
                <div className="col-12 col-md-6">
                  <InputField formik={formik} name="user_id">
                    <label>Student</label>
                    <Select
                      value={formik.values.user_id}
                      onChange={(item) => {
                        formik.setFieldValue('user_id', Number(item?.value))
                      }}
                      placeholder="Users"
                      options={[
                        { label: 'All students', value: undefined },
                        ...users.map((x) => ({
                          label: `${x.first_name} ${x.last_name}`,
                          value: x.id,
                        })),
                      ]}
                    />
                  </InputField>
                </div>
              )}
            </div>
          )}

          <div className="row">
            <div className="col-12 col-md-4">
              <InputField formik={formik} name="started_at">
                <label>Start</label>
                <DatePicker
                  showTimeSelect
                  dateFormat="MMMM d, yyyy h:mm aa"
                  selected={formik.values.started_at}
                  onChange={(date) => {
                    formik.setFieldValue('started_at', date)
                  }}
                />
              </InputField>
            </div>

            <div className="col-12 col-md-4">
              <InputField formik={formik} name="finished_at">
                <label>End</label>
                <DatePicker
                  showTimeSelect
                  dateFormat="MMMM d, yyyy h:mm aa"
                  selected={formik.values.finished_at}
                  onChange={(date) => {
                    formik.setFieldValue('finished_at', date)
                  }}
                />
              </InputField>
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-md-4">
              <InputField formik={formik} name="recurrence_type">
                <label>Repeat</label>
                <Select
                  value={formik.values.recurrence_type}
                  onChange={(item) => {
                    formik.setFieldValue('recurrence_type', Number(item?.value))
                  }}
                  placeholder="Repeat"
                  options={[
                    { label: 'Single', value: 1 },
                    { label: 'Daily', value: 2 },
                    { label: 'Weekly', value: 3 },
                    { label: 'Every weekday', value: 4 },
                  ]}
                />
              </InputField>
            </div>
            <div className="col-12 col-md-4">
              <InputField formik={formik} name="priority">
                <label>Priority</label>
                <Select
                  value={formik.values.priority}
                  onChange={(item) => {
                    formik.setFieldValue('priority', item?.value)
                  }}
                  placeholder="Priority"
                  options={[
                    { label: 'Urgently', value: 'urgently' },
                    { label: 'Smile', value: 'smile' },
                    { label: 'Star', value: 'star' },
                    { label: 'Regular weekday', value: 'regular' },
                  ]}
                />
              </InputField>
            </div>
          </div>

          {/*
          <div class="col-lg-3 col-md-4 col-sm-6">
            <div class="input-block">
              <label htmlFor="q-3">Priority</label>
              <div class="select selectIcons">
                <div class="select-top">
                  <span class="select-text"><i class="hb-ico select-ico select-1-ico"></i>Regular</span>
                </div>
                <div class="select-info">
                  <ul>
                    <li><a href="#"><i class="hb-ico select-ico select-2-ico"></i>Urgently</a></li>
                    <li><a href="#"><i class="hb-ico select-ico select-3-ico"></i>Smile</a></li>
                    <li><a href="#"><i class="hb-ico select-ico select-4-ico"></i>Star</a></li>
                    <li><a href="#"><i class="hb-ico select-ico select-1-ico"></i>Regular</a></li>
                  </ul>
                </div>
                <input type="text" class="select-value" />
              </div>
            </div>
          </div> */}

          <div className="row">
            <div className="col-12 col-md-12">
              <InputField formik={formik} name="content">
                <label>Content</label>
                <Textarea
                  placeholder="Content"
                  onChange={formik.handleChange}
                  value={formik.values.content}
                  name="content"
                />
              </InputField>
            </div>
          </div>

          <div className="row">
            <div className="col-12 col-md-12">
              <InputField formik={formik} name="Note">
                <label>Note</label>
                <Textarea
                  placeholder="Note"
                  onChange={formik.handleChange}
                  value={formik.values.Note}
                  name="Note"
                />
              </InputField>
            </div>
          </div>

          <div className="buttons-row">
            <Link class="second-btn" to={routes.timetable.index}>
              Cancel
            </Link>
            <button
              type="button"
              onClick={handleSubmit}
              className="primary-btn"
            >
              {formik.isSubmitting ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ScheduleForm
